import { useEffect, useMemo, useState, type KeyboardEvent } from "react"
import { router } from "@inertiajs/react"
import { CircleHelp, Eye, Pencil, Trash2 } from "lucide-react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { InfoTooltip } from "@/components/ui/info-tooltip"
import { MessageMarkdown } from "@/components/domain/message-markdown"
import type { FaqEntry } from "@/types/faq-entry"

type Props = {
  faqEntry: FaqEntry | null
  agentId: number
  topicSuggestions: string[]
  keywordSuggestions: string[]
  isDraft: boolean
  onSaved: () => void
  onCancel: () => void
  onDelete: () => void
}

const BLANK = { name: "", details: "", active: true }
type DetailsMode = "write" | "preview" | "help"

function normalizeKeyword(input: string): string {
  return input.trim().toLowerCase().replace(/[^a-z0-9_ -]+/g, "").replace(/\s+/g, "_").replace(/^_+|_+$/g, "")
}

function normalizeTopic(input: string): string {
  return input.trim().replace(/\s+/g, " ")
}

export function FaqEntryForm({
  faqEntry, agentId, topicSuggestions, keywordSuggestions,
  isDraft, onSaved, onCancel, onDelete,
}: Props) {
  const [fields, setFields] = useState(BLANK)
  const [submitting, setSubmitting] = useState(false)
  const [topics, setTopics] = useState<string[]>([])
  const [keywords, setKeywords] = useState<string[]>([])
  const [topicDraft, setTopicDraft] = useState("")
  const [keywordDraft, setKeywordDraft] = useState("")
  const [detailsMode, setDetailsMode] = useState<DetailsMode>("write")

  const topicByName = useMemo(() => {
    return new Map(topicSuggestions.map((topic) => [topic.toLowerCase(), topic]))
  }, [topicSuggestions])

  const canonicalTopic = (raw: string) => {
    return topicByName.get(normalizeTopic(raw).toLowerCase()) ?? null
  }

  useEffect(() => {
    if (faqEntry) {
      setFields({ name: faqEntry.name, details: faqEntry.details, active: faqEntry.active })
      setTopics(Array.isArray(faqEntry.topics) ? faqEntry.topics.map(canonicalTopic).filter((topic): topic is string => !!topic) : [])
      setKeywords(Array.isArray(faqEntry.keywords) ? [...faqEntry.keywords] : [])
    } else {
      setFields(BLANK)
      setTopics([])
      setKeywords([])
    }
    setTopicDraft("")
    setKeywordDraft("")
    setDetailsMode("write")
  }, [faqEntry?.id, isDraft, topicSuggestions])

  const addTopic = (raw: string) => {
    const value = canonicalTopic(raw)
    if (!value || topics.includes(value)) { setTopicDraft(""); return }
    setTopics((prev) => [...prev, value])
    setTopicDraft("")
  }
  const addKeyword = (raw: string) => {
    const value = normalizeKeyword(raw)
    if (!value || keywords.includes(value)) { setKeywordDraft(""); return }
    setKeywords((prev) => [...prev, value])
    setKeywordDraft("")
  }
  const removeTopic = (value: string) => setTopics((prev) => prev.filter((x) => x !== value))
  const removeKeyword = (value: string) => setKeywords((prev) => prev.filter((x) => x !== value))

  const onKeywordKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault()
      if (keywordDraft.trim()) addKeyword(keywordDraft)
    } else if (e.key === "Backspace" && keywordDraft === "" && keywords.length > 0) {
      removeKeyword(keywords[keywords.length - 1])
    }
  }

  const filteredTopicSuggestions = useMemo(() => {
    const q = topicDraft.trim().toLowerCase()
    if (!q) return topicSuggestions.filter((s) => !topics.includes(s)).slice(0, 6)
    return topicSuggestions.filter((s) => s.toLowerCase().includes(q) && !topics.includes(s)).slice(0, 6)
  }, [topicDraft, topicSuggestions, topics])

  const filteredKeywordSuggestions = useMemo(() => {
    const q = keywordDraft.trim().toLowerCase()
    if (!q) return []
    return keywordSuggestions.filter((s) => s.includes(q) && !keywords.includes(s)).slice(0, 6)
  }, [keywordDraft, keywordSuggestions, keywords])

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    setSubmitting(true)
    const payload = {
      faq_entry: {
        agent_id: agentId,
        name: fields.name,
        details: fields.details,
        active: fields.active,
        topics,
        keywords,
      },
    }
    const onFinish = () => { setSubmitting(false); onSaved() }
    if (isDraft || !faqEntry) {
      router.post("/faq_entries", payload, { preserveScroll: true, onFinish })
    } else {
      router.patch(`/faq_entries/${faqEntry.id}`, payload, { preserveScroll: true, onFinish })
    }
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full min-h-0 flex-col">
      <Card className="flex h-full min-h-0 flex-col p-0">
        <header className="border-b border-border p-4">
          <div className="flex items-center gap-2">
            <Pencil size={16} strokeWidth={2} className="text-app-muted" />
            <h2 className="text-base font-semibold text-app-text">{isDraft ? "New FAQ entry" : "Edit FAQ entry"}</h2>
          </div>
          <p className="mt-1 text-xs text-app-muted">
            {isDraft
              ? "Create a canonical answer the agent can fetch by FAQ lookup."
              : "Edit the answer body, active state, topics, and lookup tags."}
          </p>
        </header>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            <Section number={1} title="Answer">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-[minmax(0,1fr)_140px]">
                <div>
                  <Label>Name</Label>
                  <input
                    type="text"
                    required
                    value={fields.name}
                    onChange={(e) => setFields((f) => ({ ...f, name: e.target.value }))}
                    placeholder="Refund policy"
                    className="w-full rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <label className="flex min-h-10 items-center gap-2 rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text">
                    <input
                      type="checkbox"
                      checked={fields.active}
                      onChange={(e) => setFields((f) => ({ ...f, active: e.target.checked }))}
                      className="h-4 w-4 accent-brand-red"
                    />
                    Active
                  </label>
                </div>
              </div>

              <div>
                <div className="mb-1 flex flex-wrap items-center justify-between gap-2">
                  <Label>
                    Details
                    <InfoTooltip side="bottom">
                      You can include Markdown if your client supports rendering it. The stored answer remains plain text.
                    </InfoTooltip>
                  </Label>
                  <div className="inline-flex rounded-control border border-border bg-app-bg p-0.5">
                    <ModeButton
                      active={detailsMode === "write"}
                      onClick={() => setDetailsMode("write")}
                      icon={<Pencil size={12} strokeWidth={2} />}
                      label="Write"
                    />
                    <ModeButton
                      active={detailsMode === "preview"}
                      onClick={() => setDetailsMode("preview")}
                      icon={<Eye size={12} strokeWidth={2} />}
                      label="Preview"
                    />
                    <ModeButton
                      active={detailsMode === "help"}
                      onClick={() => setDetailsMode("help")}
                      icon={<CircleHelp size={12} strokeWidth={2} />}
                      label="Help"
                    />
                  </div>
                </div>

                {detailsMode === "write" && (
                  <>
                    <textarea
                      required
                      rows={12}
                      value={fields.details}
                      onChange={(e) => setFields((f) => ({ ...f, details: e.target.value }))}
                      placeholder="Canonical answer text. Markdown is allowed when your client supports it."
                      className="w-full resize-y rounded-control border border-border bg-app-bg px-3 py-2 text-sm text-app-text focus:border-brand-red focus:outline-none"
                    />
                    <p className="mt-1 text-xs text-app-muted">
                      The agent fetches and presents this body verbatim. Formatting depends on the client renderer.
                    </p>
                  </>
                )}

                {detailsMode === "preview" && (
                  <div className="min-h-[288px] rounded-control border border-border bg-app-bg p-3 text-sm text-app-text">
                    {fields.details.trim() ? (
                      <MessageMarkdown content={fields.details} />
                    ) : (
                      <p className="text-app-muted">Nothing to preview yet.</p>
                    )}
                  </div>
                )}

                {detailsMode === "help" && (
                  <div className="rounded-control border border-border bg-app-bg p-3 text-sm text-app-text">
                    <div className="mb-2 text-xs text-app-muted">
                      Common Markdown that most client renderers support:
                    </div>
                    <pre className="overflow-x-auto rounded-control border border-border bg-app-panel p-3 font-mono text-xs leading-relaxed text-app-text">{`**bold**
_italic_
[Link text](https://example.com)

- Bullet item
- Another item

1. First step
2. Second step

\`inline code\`

> Note or policy callout`}</pre>
                  </div>
                )}
              </div>
            </Section>

            <Section number={2} title="Lookup metadata">
              <div className="space-y-4">
                <TopicPicker
                  values={topics}
                  draft={topicDraft}
                  suggestions={filteredTopicSuggestions}
                  allTopicsCount={topicSuggestions.length}
                  onDraftChange={setTopicDraft}
                  onAdd={addTopic}
                  onRemove={removeTopic}
                />

                <ChipInput
                  label="Tags / keywords"
                  tooltip="Search hints shown to lookup_faq before it fetches the full body. Stored in AgentFaqEntry.keywords."
                  values={keywords}
                  draft={keywordDraft}
                  suggestions={filteredKeywordSuggestions}
                  placeholder="Add tags or keywords (Enter or , to add)"
                  onDraftChange={setKeywordDraft}
                  onKeyDown={onKeywordKeyDown}
                  onBlur={() => keywordDraft.trim() && addKeyword(keywordDraft)}
                  onAdd={addKeyword}
                  onRemove={removeKeyword}
                />
              </div>
            </Section>

            {!isDraft && faqEntry?.imported && (
              <Section number={3} title="Import source">
                <div className="flex flex-wrap items-center gap-2 text-sm text-app-muted">
                  <Badge tone="info">Imported</Badge>
                  <span>{faqEntry.supportPlatformConnectionName ?? "Support platform"}</span>
                  {faqEntry.externalId && <span className="font-mono text-xs">{faqEntry.externalId}</span>}
                  {faqEntry.sourceUpdatedAt && <span>Updated at source {new Date(faqEntry.sourceUpdatedAt).toLocaleString()}</span>}
                </div>
              </Section>
            )}
          </div>
        </div>

        <footer className="flex items-center justify-between border-t border-border p-4">
          <div>
            {!isDraft && faqEntry && (
              <Button type="button" variant="ghost" onClick={onDelete} className="border-status-error/40 text-status-error hover:bg-status-error/10">
                <Trash2 size={14} strokeWidth={2} className="mr-1.5" /> Delete FAQ entry
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button type="button" variant="ghost" onClick={onCancel}>Cancel</Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? "Saving..." : "Save changes"}
            </Button>
          </div>
        </footer>
      </Card>
    </form>
  )
}

function ChipInput({
  label, tooltip, values, draft, suggestions, placeholder,
  onDraftChange, onKeyDown, onBlur, onAdd, onRemove,
}: {
  label: string
  tooltip: string
  values: string[]
  draft: string
  suggestions: string[]
  placeholder: string
  onDraftChange: (v: string) => void
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void
  onBlur: () => void
  onAdd: (v: string) => void
  onRemove: (v: string) => void
}) {
  return (
    <div>
      <Label>
        {label}
        <InfoTooltip side="bottom">{tooltip}</InfoTooltip>
      </Label>
      <div className="flex flex-wrap items-center gap-1.5 rounded-control border border-border bg-app-bg p-2">
        {values.map((value) => (
          <span key={value} className="inline-flex items-center gap-1 rounded-control border border-border bg-app-panel px-2 py-0.5 text-xs text-app-text">
            {value}
            <button type="button" onClick={() => onRemove(value)} aria-label={`Remove ${value}`} className="opacity-70 transition hover:opacity-100">x</button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder={values.length === 0 ? placeholder : ""}
          className="min-w-[120px] flex-1 bg-transparent px-1 py-0.5 text-sm text-app-text placeholder:text-app-muted focus:outline-none"
        />
      </div>
      {suggestions.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1">
          <span className="text-[11px] text-app-muted">Suggestions:</span>
          {suggestions.map((suggestion) => (
            <button
              key={suggestion}
              type="button"
              onClick={() => onAdd(suggestion)}
              className="rounded-control border border-border bg-app-bg px-1.5 py-0.5 text-[11px] text-app-muted transition hover:border-brand-red hover:text-app-text"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function TopicPicker({
  values, draft, suggestions, allTopicsCount,
  onDraftChange, onAdd, onRemove,
}: {
  values: string[]
  draft: string
  suggestions: string[]
  allTopicsCount: number
  onDraftChange: (v: string) => void
  onAdd: (v: string) => void
  onRemove: (v: string) => void
}) {
  const addSuggestion = (value: string) => {
    onAdd(value)
    onDraftChange("")
  }

  return (
    <div>
      <Label>
        Topics
        <InfoTooltip side="bottom">
          Pick from this agent's created Topics. Free-form FAQ topics are not allowed.
        </InfoTooltip>
      </Label>

      <div className="rounded-control border border-border bg-app-bg p-2">
        {values.length > 0 && (
          <div className="mb-2 flex flex-wrap items-center gap-1.5">
            {values.map((value) => (
              <span key={value} className="inline-flex items-center gap-1 rounded-control border border-border bg-app-panel px-2 py-0.5 text-xs text-app-text">
                {value}
                <button type="button" onClick={() => onRemove(value)} aria-label={`Remove ${value}`} className="opacity-70 transition hover:opacity-100">x</button>
              </span>
            ))}
          </div>
        )}

        {allTopicsCount > 0 ? (
          <>
            <input
              type="text"
              value={draft}
              onChange={(e) => onDraftChange(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault()
                  if (suggestions[0]) addSuggestion(suggestions[0])
                } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
                  onRemove(values[values.length - 1])
                }
              }}
              placeholder="Find topic"
              className="w-full bg-transparent px-1 py-0.5 text-sm text-app-text placeholder:text-app-muted focus:outline-none"
            />
            {suggestions.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {suggestions.map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    onClick={() => addSuggestion(suggestion)}
                    className="rounded-control border border-border bg-app-panel px-2 py-1 text-xs text-app-muted transition hover:border-brand-red hover:text-app-text"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="px-1 py-0.5 text-sm text-app-muted">No topics created yet.</div>
        )}
      </div>
    </div>
  )
}

function ModeButton({ active, onClick, icon, label }: {
  active: boolean
  onClick: () => void
  icon: React.ReactNode
  label: string
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={[
        "inline-flex min-h-8 items-center gap-1.5 rounded-control px-2.5 py-1 text-xs transition",
        active ? "bg-white/10 text-app-text" : "text-app-muted hover:bg-white/5 hover:text-app-text",
      ].join(" ")}
    >
      {icon}
      {label}
    </button>
  )
}

function Section({ number, title, children }: {
  number: number
  title: string
  children: React.ReactNode
}) {
  return (
    <Card variant="inset" className="space-y-3 p-4">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full border border-border bg-app-panel2 text-[11px] font-semibold text-app-muted">{number}</span>
        <h3 className="text-sm font-semibold text-app-text">{title}</h3>
      </div>
      <div className="space-y-3">{children}</div>
    </Card>
  )
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <div className="mb-1 flex items-center gap-1 text-[11px] uppercase tracking-wide text-app-muted">
      {children}
    </div>
  )
}
